import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { IMAGES } from "../../lib/assets";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROLES = ["Luxury Agent", "Builder", "Developer", "Commercial", "Investor", "Residential"];

export const Signup = () => {
  const [form, setForm] = useState({ name: "", email: "", role: "Luxury Agent", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast("Almost there", { description: "Please share your name and email." });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
      toast("Welcome to the list", { description: "We'll be in touch as we approach launch." });
      setForm({ name: "", email: "", role: "Luxury Agent", message: "" });
    } catch (err) {
      toast("Something went wrong", { description: "Please try again in a moment." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="connect" className="relative bg-ink py-28 md:py-40 border-t border-charcoal/40" data-testid="signup">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          {/* Left copy + image */}
          <div className="lg:col-span-5">
            <Reveal><div className="overline mb-6">Private Introductions</div></Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif font-light text-ivory text-5xl md:text-6xl leading-[1] tracking-tight mb-8">
                Let's begin the conversation.
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="text-taupe font-light leading-relaxed max-w-md mb-10">
                We'd love to connect with agents, builders, developers and commercial clients preparing for
                future transactions across Texas.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="relative overflow-hidden aspect-[16/10] max-w-md">
                <img src={IMAGES.conference} alt="Private conference room" className="h-full w-full object-cover" />
                <div className="absolute inset-0 ring-1 ring-inset ring-gold/20" />
              </div>
            </Reveal>
          </div>

          {/* Right form */}
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal delay={0.15}>
              {done ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="border border-gold/40 bg-card p-12 text-center"
                  data-testid="signup-success"
                >
                  <div className="font-serif text-gold text-6xl mb-6">✦</div>
                  <h3 className="font-serif text-3xl text-ivory font-light mb-4">You're on the list</h3>
                  <p className="text-taupe font-light">
                    Thank you. We'll reach out personally as we approach launch.
                  </p>
                  <button
                    onClick={() => setDone(false)}
                    data-testid="signup-reset-btn"
                    className="mt-8 text-gold text-[0.72rem] uppercase tracking-[0.2em] border-b border-gold/50 pb-1"
                  >
                    Submit Another
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={submit} className="space-y-8" data-testid="signup-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                    <Field label="Full Name" testid="signup-name">
                      <input
                        value={form.name}
                        onChange={set("name")}
                        data-testid="signup-name-input"
                        placeholder="Your name"
                        className="w-full bg-transparent border-b border-charcoal py-3 text-ivory font-light placeholder:text-taupe/40 focus:border-gold outline-none transition-colors duration-300"
                      />
                    </Field>
                    <Field label="Email" testid="signup-email">
                      <input
                        type="email"
                        value={form.email}
                        onChange={set("email")}
                        data-testid="signup-email-input"
                        placeholder="you@company.com"
                        className="w-full bg-transparent border-b border-charcoal py-3 text-ivory font-light placeholder:text-taupe/40 focus:border-gold outline-none transition-colors duration-300"
                      />
                    </Field>
                  </div>

                  <div>
                    <div className="overline text-[0.6rem] mb-4 text-taupe">I am a</div>
                    <div className="flex flex-wrap gap-3" data-testid="signup-roles">
                      {ROLES.map((r) => (
                        <button
                          key={r}
                          type="button"
                          onClick={() => setForm((f) => ({ ...f, role: r }))}
                          data-testid={`signup-role-${r.toLowerCase().replace(" ", "-")}`}
                          className={`px-5 py-2.5 text-[0.72rem] uppercase tracking-[0.15em] border transition-colors duration-300 ${
                            form.role === r
                              ? "border-gold bg-gold text-ink"
                              : "border-charcoal text-taupe hover:border-gold/60 hover:text-ivory"
                          }`}
                        >
                          {r}
                        </button>
                      ))}
                    </div>
                  </div>

                  <Field label="Message (optional)" testid="signup-message">
                    <textarea
                      value={form.message}
                      onChange={set("message")}
                      rows={3}
                      data-testid="signup-message-input"
                      placeholder="Tell us about what you're preparing for…"
                      className="w-full bg-transparent border-b border-charcoal py-3 text-ivory font-light placeholder:text-taupe/40 focus:border-gold outline-none transition-colors duration-300 resize-none"
                    />
                  </Field>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="signup-submit-btn"
                    className="group relative overflow-hidden bg-ivory text-ink text-[0.72rem] uppercase tracking-[0.2em] px-10 py-4 disabled:opacity-60"
                  >
                    <span className="relative z-10">{loading ? "Sending…" : "Request Introduction"}</span>
                    <span className="absolute inset-0 bg-gold translate-y-full transition-transform duration-500 group-hover:translate-y-0" />
                  </button>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="overline text-[0.6rem] text-taupe block mb-2">{label}</span>
    {children}
  </label>
);
